import React, { useMemo } from 'react'
import { Autocomplete, Avatar, Chip, TextField } from '@mui/material'
import { IRecipientCandidate } from 'interfaces/IFleetMonitoring'
import { recipientColor, recipientInitials, recipientLabel } from '../recipientDisplay'

interface IRecipientsInputProps {
  /** Selected recipients as platform user IDs. */
  value: string[]
  onChange: (userIds: string[]) => void
  /** Eligible users to pick from (company insurer users). */
  candidates: IRecipientCandidate[]
  placeholder?: string
  disabled?: boolean
  size?: 'small' | 'medium'
  className?: string
}

const RecipientsInput: React.FunctionComponent<IRecipientsInputProps> = ({
  value,
  onChange,
  candidates,
  placeholder = 'Search users to add',
  disabled,
  size = 'small',
  className,
}) => {
  const byId = useMemo(() => {
    const map = new Map<string, IRecipientCandidate>()
    candidates.forEach(c => map.set(c.userId, c))
    return map
  }, [candidates])

  // Map stored user IDs back to candidate objects; drop any that no longer resolve
  // (e.g. a recipient that's no longer an eligible user).
  const selected = useMemo(
    () => value.map(id => byId.get(id)).filter((c): c is IRecipientCandidate => !!c),
    [value, byId],
  )

  return (
    <Autocomplete
      multiple
      disablePortal={false}
      filterSelectedOptions
      disabled={disabled}
      options={candidates}
      value={selected}
      getOptionLabel={recipientLabel}
      isOptionEqualToValue={(a, b) => a.userId === b.userId}
      onChange={(_, next) => onChange(next.map(c => c.userId))}
      size={size}
      className={className}
      sx={{
        '& .MuiOutlinedInput-root': {
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          rowGap: '6px',
          paddingTop: '6px',
          paddingBottom: '6px',
        },
        '& .MuiAutocomplete-input': { minWidth: '120px' },
      }}
      renderOption={(props, c) => {
        const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
          key?: string
        }
        return (
          <li {...optionProps} key={c.userId} style={{ display: 'flex', gap: '10px' }}>
            <Avatar
              sx={{ width: 24, height: 24, fontSize: 11, bgcolor: recipientColor(c.userId) }}
            >
              {recipientInitials(c)}
            </Avatar>
            <span>{recipientLabel(c)}</span>
          </li>
        )
      }}
      renderTags={(tags, getTagProps) =>
        tags.map((c, index) => {
          const { key, ...tagProps } = getTagProps({ index })
          return (
            <Chip
              {...tagProps}
              key={c.userId}
              size="small"
              avatar={
                <Avatar sx={{ bgcolor: recipientColor(c.userId), color: '#fff !important' }}>
                  {recipientInitials(c)}
                </Avatar>
              }
              label={recipientLabel(c)}
            />
          )
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          size={size}
          placeholder={value.length === 0 ? placeholder : ''}
        />
      )}
    />
  )
}

export default RecipientsInput
